// Phase 12.3 P0 — wraps react-router-dom's `useNavigate` so every
// click-driven navigation goes through React.startTransition.
//
// Why this exists: routes in this app are lazy() and rendered inside
// <Suspense>. A synchronous user event (click handler) that triggers
// `navigate(...)` will, on first-visit, cause the new route to suspend
// while its chunk downloads — React 18 treats that as "Suspense was
// hit by synchronous input" and throws minified error 426. The visible
// symptom is a brief blank UI or a stuck loader (no error boundary
// catches it because it's a flush-time warning, not a thrown error).
//
// `<Link>` already does this internally; `navigate()` doesn't. Pages
// that build the destination URL imperatively (`navigate('/devices/' + id)`
// or `navigate({ search: '?settings=1' })`) should call this hook
// instead of `useNavigate`.
//
// Drop-in replacement: same call signature as `useNavigate`. Passing
// a number (history go(-1)) still works synchronously since that path
// does not mount new lazy components.

import { startTransition, useCallback } from 'react';
import { type NavigateOptions, type To, useNavigate } from 'react-router-dom';

export function useSafeNavigate() {
  const navigate = useNavigate();
  return useCallback((to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }
    startTransition(() => {
      navigate(to as To, options);
    });
  }, [navigate]);
}
